import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useState } from "react";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SetupChecklist } from "@/components/app/setup/setup-checklist";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye, EyeOff } from "@/lib/icons";
import { usePolicy } from "@/lib/policy";
import { useSetSetupPanel, useSetupBoard } from "@/lib/session/setup-checklist";

export const Route = createFileRoute("/_authenticated/getting-started")({
  head: () => ({ meta: [{ title: "Setup | People AI" }] }),
  component: SetupPage,
});

/** The organization's setup checklist as a page of its own: the rail's Setup row and the
 * dashboard lead here, and every screen a step opens leads back. */
function SetupPage() {
  const { subject, can } = usePolicy();
  const board = useSetupBoard();
  const setPanel = useSetSetupPanel();
  const [confirmHide, setConfirmHide] = useState(false);
  if (subject.organization === null || !can("settings:manage")) return <Navigate to="/" replace />;

  const data = board.data;
  if (!data) {
    if (board.isError) {
      return (
        <ReadFailed
          message="The setup checklist could not be loaded."
          error={board.error}
          onRetry={() => void board.refetch()}
          retrying={board.isRefetching}
        />
      );
    }
    return <Skeleton className="h-96 w-full rounded-xl" />;
  }

  const { complete, skipped, total, percent } = data.summary;
  const hidden = data.panel.hiddenAt !== null;
  const meta =
    percent >= 100
      ? "Everything on it is done or set aside."
      : `${complete + skipped} of ${total} done. Only what this organization uses is on it.`;
  return (
    <>
      <PageHeader
        title="Set up People AI"
        meta={meta}
        actions={
          hidden ? (
            <Button
              variant="outline"
              disabled={setPanel.isPending}
              onClick={() => setPanel.mutate({ hidden: false })}
              data-testid="button-show-setup"
            >
              <Eye className="size-4" aria-hidden="true" />
              Show setup in the menu
            </Button>
          ) : percent < 100 ? (
            <Button variant="ghost" onClick={() => setConfirmHide(true)}>
              <EyeOff className="size-4" aria-hidden="true" />
              Hide setup
            </Button>
          ) : null
        }
      />
      <div className="bg-card max-w-3xl rounded-xl border py-3">
        <SetupChecklist board={data} />
      </div>
      <AlertDialog open={confirmHide} onOpenChange={setConfirmHide}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide setup?</AlertDialogTitle>
            <AlertDialogDescription>
              The Setup row and its reminders go away for you. This page stays under Settings,
              Organization, where you can show it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmHide(false);
                setPanel.mutate({ hidden: true });
              }}
            >
              Hide setup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
